import { useEffect } from 'react';

const TO_RADIANS = Math.PI / 180;

export function useDebounceEffect(fn, waitTime, deps) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(() => {
      fn.apply(undefined, deps);
    }, waitTime);

    return () => {
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export async function canvasPreview(image, canvas, crop, scale = 1, rotate = 0) {
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = window.devicePixelRatio;

  canvas.width = (crop.width - 1) * scaleX * pixelRatio;
  canvas.height = (crop.height - 1) * scaleY * pixelRatio;

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;

  const rotateRads = rotate * TO_RADIANS;
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();

  ctx.translate(-cropX, -cropY);
  ctx.translate(centerX, centerY);
  ctx.rotate(rotateRads);
  ctx.scale(scale, scale);
  ctx.translate(-centerX, -centerY);
  ctx.drawImage(
    image,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight
  );

  ctx.restore();
}

// Helper to calculate centered crop
export function getCenteredAspectCrop(mediaWidth, mediaHeight, aspect) {
  // Calculate the maximum crop size that fits inside the image for the given aspect
  let cropWidth, cropHeight;

  if (mediaWidth / mediaHeight > aspect) {
    // Image is wider than aspect: crop by height
    cropHeight = mediaHeight;
    cropWidth = cropHeight * aspect;
  } else {
    // Image is taller than aspect: crop by width
    cropWidth = mediaWidth;
    cropHeight = cropWidth / aspect;
  }

  // Convert to percentage values for react-image-crop
  const x = ((mediaWidth - cropWidth) / 2 / mediaWidth) * 100;
  const y = ((mediaHeight - cropHeight) / 2 / mediaHeight) * 100;
  const width = (cropWidth / mediaWidth) * 100;
  const height = (cropHeight / mediaHeight) * 100;

  return {
    unit: '%',
    x,
    y,
    width,
    height,
    aspect,
  };
}

const OUTPUT_TYPE = 'image/jpeg';
const OUTPUT_QUALITY = 1;

// OffscreenCanvas came to Safari only in 16.4; older browsers draw and
// encode through a detached canvas element instead.
const createOutputCanvas = (width, height) => {
  if (typeof OffscreenCanvas !== 'undefined') {
    return new OffscreenCanvas(width, height);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

const encodeCanvas = canvas =>
  typeof OffscreenCanvas !== 'undefined' && canvas instanceof OffscreenCanvas
    ? canvas.convertToBlob({ type: OUTPUT_TYPE, quality: OUTPUT_QUALITY })
    : new Promise((resolve, reject) => {
        canvas.toBlob(
          blob => (blob ? resolve(blob) : reject(new Error('The canvas gave no image'))),
          OUTPUT_TYPE,
          OUTPUT_QUALITY
        );
      });

// Draws the preview canvas (already cropped by canvasPreview) at the image's
// natural resolution and resolves with it as a JPEG File called fileName.
// Rejects when the image can't be drawn or encoded.
export function getCroppedImg(image, crop, fileName, previewCanvas) {
  return new Promise((resolve, reject) => {
    if (!crop || !image) {
      reject(new Error('No crop or image'));
      return;
    }
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const output = createOutputCanvas(crop.width * scaleX, crop.height * scaleY);
    const ctx = output.getContext('2d');
    if (!ctx) {
      reject(new Error('No 2d context'));
      return;
    }

    ctx.drawImage(
      previewCanvas,
      0,
      0,
      previewCanvas.width,
      previewCanvas.height,
      0,
      0,
      output.width,
      output.height
    );

    encodeCanvas(output)
      .then(blob => {
        resolve(new File([blob], fileName, { type: OUTPUT_TYPE }));
      })
      .catch(err => {
        reject(new Error('Error converting to blob: ' + err.message));
      });
  });
}
